/*
 * Estimated shipping cost in the cart.
 * Requests live rates from /cart/shipping_rates.json for the chosen
 * country / province / postal code and shows the cheapest one.
 *
 * The element lives inside the containers cart.js and cart-drawer.js re-render
 * (#main-cart-footer .js-contents / .drawer__inner), so every quantity change
 * destroys and recreates it. The last address is kept in localStorage and the
 * estimate is recalculated automatically on reconnect.
 */
if (!customElements.get('shipping-estimator')) {
  const STORAGE_KEY = 'shipping-estimator-address';

  customElements.define(
    'shipping-estimator',
    class ShippingEstimator extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.ratesUrl = this.dataset.ratesUrl;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.strings = this.parseStrings();

        this.countryEl = this.querySelector('[data-se-country]');
        this.provinceEl = this.querySelector('[data-se-province]');
        this.provinceContainer = this.provinceEl ? this.provinceEl.closest('.shipping-estimator__field') : null;
        this.zipEl = this.querySelector('[data-se-zip]');
        this.submitEl = this.querySelector('[data-se-submit]');
        this.resultsEl = this.querySelector('[data-se-results]');
        this.summaryEl = this.querySelector('[data-se-summary]');
        this.detailsEl = this.querySelector('.shipping-estimator__details');

        if (!this.countryEl || !this.zipEl || !this.submitEl) return;

        this.initProvinceSelector();
        this.restoreAddress();

        this.submitEl.addEventListener('click', () => this.estimate());
        // The fields sit next to the cart form — Enter must not submit it (that would start checkout).
        this.zipEl.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.estimate();
        });

        // This element also lives in the site-wide cart drawer, so connectedCallback
        // runs on every page load with items in the cart — auto-estimating here would
        // hit the live rates endpoint for a panel nobody has opened. Wait until the
        // shopper actually expands the accordion, and only do it once per connection.
        if (this.detailsEl) {
          this.detailsEl.addEventListener('toggle', () => {
            if (!this.detailsEl.open || this.autoEstimated) return;
            if (this.savedAddress && this.savedAddress.zip) {
              this.autoEstimated = true;
              this.estimate({ silent: true });
            }
          });
        }
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      parseStrings() {
        const script = this.querySelector('[data-se-strings]');
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return {};
        }
      }

      initProvinceSelector() {
        if (!this.provinceEl || !this.provinceContainer) return;

        // Theme helper (assets/global.js) — same one the address forms use.
        if (window.Shopify && window.Shopify.CountryProvinceSelector) {
          new window.Shopify.CountryProvinceSelector(this.countryEl.id, this.provinceEl.id, {
            hideElement: this.provinceContainer.id,
          });
        } else {
          this.provinceContainer.style.display = 'none';
        }
      }

      restoreAddress() {
        this.savedAddress = null;
        let saved = null;
        try {
          saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        } catch (error) {
          saved = null;
        }
        if (!saved) return;

        this.savedAddress = saved;
        if (saved.country) {
          this.countryEl.value = saved.country;
          this.countryEl.dispatchEvent(new Event('change'));
        }
        if (saved.province && this.provinceEl) this.provinceEl.value = saved.province;
        if (saved.zip) this.zipEl.value = saved.zip;
      }

      saveAddress(address) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(address));
        } catch (error) {
          // Private mode / storage full — the estimate still works for this session.
        }
      }

      currentAddress() {
        const provinceVisible = this.provinceContainer && this.provinceContainer.style.display !== 'none';
        return {
          country: this.countryEl.value,
          province: provinceVisible && this.provinceEl ? this.provinceEl.value : '',
          zip: this.zipEl.value.trim(),
        };
      }

      estimate({ silent = false } = {}) {
        const address = this.currentAddress();
        // country_option_tags starts with a "---" divider between popular and all countries.
        if (!address.country || address.country === '---') return;

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        this.setLoading(true);
        if (!silent) this.resultsEl.innerHTML = `<p class="shipping-estimator__status">${this.strings.calculating}</p>`;
        this.summaryEl.textContent = this.strings.calculating;

        const params = new URLSearchParams({
          'shipping_address[country]': address.country,
          'shipping_address[province]': address.province,
          'shipping_address[zip]': address.zip,
        });

        fetch(`${this.ratesUrl}?${params}`, {
          signal: this.controller.signal,
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
          .then(({ ok, body }) => {
            // cart.js/cart-drawer.js can re-render the cart (and destroy this element) on any
            // cart update on the page, not just ours — a stale, disconnected instance must not
            // touch its own (now detached) DOM, or it leaves the replacement element's button stuck.
            if (!this.isConnected) return;
            if (!ok) {
              // 422 bodies look like {"zip":["Enter a valid postal code for Madrid"]} —
              // Shopify's own message is localised and names the field, so prefer it.
              this.renderFallback(this.addressError(body) || this.strings.invalid_address);
              return;
            }
            const rates = body.shipping_rates || [];
            if (!rates.length) {
              this.renderFallback(this.strings.no_rates);
              return;
            }
            this.saveAddress(address);
            this.renderRates(rates);
          })
          .catch((error) => {
            if (error.name === 'AbortError' || !this.isConnected) return;
            this.renderFallback(this.strings.no_rates);
          })
          .finally(() => {
            if (this.isConnected) this.setLoading(false);
          });
      }

      setLoading(loading) {
        this.submitEl.classList.toggle('shipping-estimator__button--loading', loading);
        this.submitEl.disabled = loading;
      }

      addressError(body) {
        if (!body || typeof body !== 'object') return '';
        const messages = Object.keys(body).reduce((all, key) => all.concat(body[key]), []);
        return messages.find((message) => typeof message === 'string') || '';
      }

      deliveryEstimate(rate) {
        const days = rate.delivery_days;
        if (!Array.isArray(days) || days.length < 2) return '';
        if (days[0] === days[1] || !this.strings.delivery_days) return '';
        return this.strings.delivery_days.replace('[min]', days[0]).replace('[max]', days[1]);
      }

      renderRates(rates) {
        const sorted = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        const cheapest = sorted[0];
        const isFree = parseFloat(cheapest.price) === 0;
        const price = isFree ? this.strings.free : this.formatMoney(cheapest.price);
        // presentment_name is the carrier name in the shopper's language.
        const name = cheapest.presentment_name || cheapest.name || cheapest.title || '';
        const days = this.deliveryEstimate(cheapest);

        let html = `<p class="shipping-estimator__rate">
            <span class="shipping-estimator__rate-name">${this.escape(name)}${
              days ? ` <span class="shipping-estimator__rate-days">${this.escape(days)}</span>` : ''
            }</span>
            <span class="shipping-estimator__rate-price${
              isFree ? ' shipping-estimator__rate-price--free' : ''
            }">${this.escape(price)}</span>
          </p>`;

        if (sorted.length > 1) {
          html += `<p class="shipping-estimator__note">${this.escape(this.strings.other_options)}</p>`;
        }

        this.resultsEl.innerHTML = html;
        this.summaryEl.textContent = price;
      }

      renderFallback(message) {
        this.resultsEl.innerHTML = `<p class="shipping-estimator__status">${this.escape(message)}</p>`;
        this.summaryEl.textContent = this.strings.summary_placeholder;
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = value == null ? '' : value;
        return div.innerHTML;
      }

      // Rate prices come back as a decimal string in the shop currency ("9.00").
      formatMoney(price) {
        const cents = Math.round(parseFloat(price) * 100);
        if (Number.isNaN(cents)) return '';

        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const withDelimiters = (number, precision, thousands, decimal) => {
          const value = (number / 100).toFixed(precision);
          const parts = value.split('.');
          return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');
        };

        const match = this.moneyFormat.match(placeholder);
        let value = '';

        switch (match && match[1]) {
          case 'amount':
            value = withDelimiters(cents, 2, ',', '.');
            break;
          case 'amount_no_decimals':
            value = withDelimiters(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(cents, 2, "'", '.');
            break;
          default:
            return (cents / 100).toFixed(2);
        }

        return this.moneyFormat.replace(placeholder, value);
      }
    }
  );
}
